import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Compass,
  Mail,
  Moon,
  Plus,
  Trash2,
  User as UserIcon,
  UserPlus,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import CalendarConnect from "@/components/CalendarConnect";

const DAYS = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

const ROLES = [
  { value: "adult", label: "Adult" },
  { value: "teen", label: "Teen" },
  { value: "child", label: "Child" },
  { value: "dependent", label: "Dependent (care recipient)" },
  { value: "other", label: "Other" },
];

const CATEGORIES = [
  { value: "work", label: "Work" },
  { value: "school", label: "School" },
  { value: "care", label: "Caregiving" },
  { value: "commute", label: "Commute" },
  { value: "chores", label: "Chores / errands" },
  { value: "sleep", label: "Sleep window" },
  { value: "social", label: "Social" },
  { value: "other", label: "Other" },
];

const STEPS = ["Welcome", "Roster", "Standing commitments", "Recovery windows", "Ready"];

// helpers
const minutesToLabel = (min) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const ampm = h >= 12 ? "PM" : "AM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
};

const uid = (prefix) => `${prefix}_${Math.random().toString(36).slice(2, 10)}`;

// ---------- Small building blocks ----------
function DraftStatePill({ state }) {
  const map = {
    pending: { label: "Editing…", tone: "text-mist border-rule" },
    saving: { label: "Saving…", tone: "text-teal border-teal/60" },
    saved: { label: "Saved", tone: "text-signalGreen border-signalGreen/60" },
    error: { label: "Save failed", tone: "text-signalRed border-signalRed/60" },
    idle: { label: "", tone: "" },
  };
  const info = map[state] || map.idle;
  if (!info.label) return null;
  return (
    <span
      data-testid="draft-state-pill"
      data-state={state}
      className={`hidden items-center border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.22em] sm:inline-flex ${info.tone}`}
    >
      {info.label}
    </span>
  );
}


function StepPill({ index, current, label, onClick }) {
  const state =
    index < current ? "done" : index === current ? "active" : "future";
  const color =
    state === "done"
      ? "border-teal text-teal"
      : state === "active"
        ? "border-amber text-amber"
        : "border-rule text-mist";
  return (
    <button
      onClick={onClick}
      data-testid={`onb-step-${index}`}
      className={`flex items-center gap-3 border-b-2 pb-3 pl-1 pr-4 text-left transition-colors duration-300 ${color}`}
    >
      <span className="font-mono text-[10px] tracking-[0.22em]">
        {String(index + 1).padStart(2, "0")}
      </span>
      <span className="font-mono text-[10px] uppercase tracking-[0.22em]">
        {label}
      </span>
    </button>
  );
}

function DayPicker({ value, onChange, testIdPrefix }) {
  const toggle = (k) =>
    onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div className="flex flex-wrap gap-2">
      {DAYS.map((d) => {
        const on = value.includes(d.key);
        return (
          <button
            key={d.key}
            type="button"
            onClick={() => toggle(d.key)}
            data-testid={`${testIdPrefix}-day-${d.key}`}
            className={`border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-200 ${
              on
                ? "border-teal bg-teal/15 text-ink"
                : "border-rule bg-obsidian text-mist hover:border-teal/50"
            }`}
          >
            {d.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------- Onboarding page ----------
export default function Onboarding() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    household_name: "My household",
    persona: null,
    members: [],
    commitments: [],
    recovery_windows: [],
    profile_complete: false,
  });

  // load existing profile once
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profile");
        setProfile({
          household_name: data.household_name || "My household",
          persona: data.persona || null,
          members: data.members?.length
            ? data.members
            : [
                {
                  member_id: uid("mbr"),
                  name: user?.name?.split(" ")[0] || "Me",
                  role: "adult",
                  weekly_hours_available: 30,
                  color: null,
                },
              ],
          commitments: data.commitments || [],
          recovery_windows: data.recovery_windows || [],
          profile_complete: data.profile_complete || false,
        });
      } catch (_) {
        // start empty
        setProfile((p) => ({
          ...p,
          members: [
            {
              member_id: uid("mbr"),
              name: user?.name?.split(" ")[0] || "Me",
              role: "adult",
              weekly_hours_available: 30,
              color: null,
            },
          ],
        }));
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.name]);

  // Debounced draft persistence — coalesce multiple edits into one PUT.
  const pendingPatch = useRef({});
  const saveTimer = useRef(null);
  const [draftState, setDraftState] = useState("idle"); // idle | pending | saving | saved | error

  const flushDraft = useCallback(async () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    const patch = pendingPatch.current;
    if (!patch || Object.keys(patch).length === 0) return;
    pendingPatch.current = {};
    setDraftState("saving");
    try {
      await api.put("/profile", patch);
      setDraftState("saved");
      // return to idle after a beat so the pill is calmly present
      setTimeout(() => {
        setDraftState((s) => (s === "saved" ? "idle" : s));
      }, 1400);
    } catch (_) {
      setDraftState("error");
      setTimeout(() => setDraftState("idle"), 2000);
    }
  }, []);

  const persistDraft = (patch, opts = {}) => {
    const next = { ...profile, ...patch };
    setProfile(next);
    if (opts.silent === true) return;

    // Merge into the pending patch (later edits to the same key win)
    pendingPatch.current = { ...pendingPatch.current, ...patch };
    setDraftState("pending");

    // Reset the debounce window every keystroke — flush after 700ms of quiet.
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flushDraft, 700);
  };

  // Flush any pending patch on unmount / navigation to avoid dropped edits.
  useEffect(() => {
    return () => {
      if (pendingPatch.current && Object.keys(pendingPatch.current).length > 0) {
        // fire and forget — no await in a cleanup fn
        api.put("/profile", pendingPatch.current).catch(() => {});
        pendingPatch.current = {};
      }
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  const finish = async () => {
    // Make sure any in-flight debounced patch is persisted before completing.
    await flushDraft();
    setSaving(true);
    try {
      await api.put("/profile", { ...profile, profile_complete: true });
      await checkAuth(); // refresh user to pick up profile_complete=true
      toast.success("Household profile locked in. Tower is warming up.");
      navigate("/app/tower", { replace: true });
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        "Could not save profile. Try again in a moment.";
      toast.error(typeof msg === "string" ? msg : "Could not save profile.");
    } finally {
      setSaving(false);
    }
  };

  const stepCount = STEPS.length;
  const canNext = useMemo(() => {
    if (step === 1) return profile.members.length > 0 && profile.members.every((m) => m.name);
    if (step === 2) return true; // commitments optional but recommended
    if (step === 3) return true; // recovery optional
    return true;
  }, [step, profile]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-obsidian text-mist font-mono text-[11px] uppercase tracking-[0.24em]">
        Loading household profile…
      </div>
    );
  }

  return (
    <main
      data-testid="onboarding-page"
      className="relative min-h-screen bg-obsidian text-ink"
    >
      {/* top bar */}
      <div className="pointer-events-none absolute inset-0 hud-grid-fine opacity-20" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-64 bg-[radial-gradient(ellipse_at_center,rgba(46,119,136,0.14),transparent_70%)]" />

      <header className="relative z-10 border-b border-rule">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6 md:px-10">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="relative flex h-6 w-6 items-center justify-center border border-teal/70">
              <Compass className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
              <span className="absolute -top-px -left-px h-1 w-1 bg-amber" />
            </span>
            <span className="font-display text-lg text-ink">Life OS</span>
            <span className="hidden font-mono text-[9px] uppercase tracking-[0.22em] text-mist sm:inline">
              &nbsp;/ onboarding
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <DraftStatePill state={draftState} />
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
              Step <span className="text-ink">{String(step + 1).padStart(2, "0")}</span>{" "}
              /&nbsp;{stepCount}
            </div>
          </div>
        </div>
        {/* progress bar */}
        <div className="mx-auto max-w-6xl px-6 pb-4 md:px-10">
          <div className="mt-2 flex gap-4 overflow-x-auto">
            {STEPS.map((s, i) => (
              <StepPill
                key={s}
                index={i}
                current={step}
                label={s}
                onClick={() => setStep(i)}
              />
            ))}
          </div>
        </div>
      </header>

      <div className="relative z-10 mx-auto max-w-6xl px-6 py-10 md:px-10 md:py-14">
        <AnimatePresence mode="wait">
          <motion.section
            key={step}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            {step === 0 && <StepWelcome user={user} />}
            {step === 1 && (
              <StepRoster
                members={profile.members}
                setMembers={(members) => persistDraft({ members })}
                household_name={profile.household_name}
                setHousehold={(household_name) => persistDraft({ household_name })}
                persona={profile.persona}
                setPersona={(persona) => persistDraft({ persona })}
                timezone={profile.timezone}
                setTimezone={(timezone) => persistDraft({ timezone })}
              />
            )}
            {step === 2 && (
              <StepCommitments
                commitments={profile.commitments}
                members={profile.members}
                setCommitments={(commitments) => persistDraft({ commitments })}
              />
            )}
            {step === 3 && (
              <StepRecovery
                windows={profile.recovery_windows}
                setWindows={(recovery_windows) => persistDraft({ recovery_windows })}
              />
            )}
            {step === 4 && <StepReady profile={profile} />}
          </motion.section>
        </AnimatePresence>

        {/* nav */}
        <div className="mt-14 flex items-center justify-between border-t border-rule pt-6">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            data-testid="onb-back-btn"
            className="inline-flex items-center gap-2 border border-rule px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-mist transition-colors duration-300 hover:border-amber hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
            Back
          </button>

          {step < stepCount - 1 ? (
            <button
              onClick={() => canNext && setStep((s) => s + 1)}
              disabled={!canNext}
              data-testid="onb-next-btn"
              className="group inline-flex items-center gap-2 bg-teal px-6 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:bg-teal-hover disabled:cursor-not-allowed disabled:opacity-40"
            >
              Continue
              <ArrowRight
                className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5"
                strokeWidth={1.5}
              />
            </button>
          ) : (
            <button
              onClick={finish}
              disabled={saving}
              data-testid="onb-finish-btn"
              className="group inline-flex items-center gap-2 bg-teal px-6 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:bg-teal-hover disabled:opacity-60"
            >
              {saving ? "Locking in…" : "Enter the tower"}
              <Check className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>
    </main>
  );
}

// ---------- Steps ----------
function StepWelcome({ user }) {
  return (
    <div className="max-w-3xl">
      <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-teal">
        Preflight briefing
      </p>
      <h1 className="mt-6 font-display text-5xl leading-[1.02] text-ink md:text-6xl">
        Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
        <br />
        <span className="italic text-mist">Let&apos;s tune your instruments.</span>
      </h1>
      <p className="mt-8 max-w-xl text-base leading-relaxed text-mist">
        Life OS reads three things about your household: who&apos;s aboard,
        what&apos;s already committed, and what you defend. We&apos;ll ask you a
        few short questions — then the tower comes online.
      </p>

      <ul className="mt-10 divide-y divide-rule border-y border-rule">
        {[
          ["01", "Roster", "Who lives in the household and how much time they have."],
          ["02", "Standing commitments", "The recurring load — work, school, sleep, care."],
          ["03", "Recovery windows", "The time you defend as untouchable."],
        ].map(([n, t, d]) => (
          <li key={n} className="grid grid-cols-12 gap-6 py-5">
            <span className="col-span-1 font-mono text-[11px] uppercase tracking-[0.24em] text-teal">
              {n}
            </span>
            <span className="col-span-3 font-display text-lg text-ink">
              {t}
            </span>
            <span className="col-span-8 text-sm text-mist">{d}</span>
          </li>
        ))}
      </ul>

      <p className="mt-8 max-w-xl text-sm leading-relaxed text-mist">
        Nothing here is judged. This is your inventory. The tower reads it —
        and only interrupts you when something matters.
      </p>
    </div>
  );
}

function StepRoster({
  members,
  setMembers,
  household_name,
  setHousehold,
  persona,
  setPersona,
  timezone,
  setTimezone,
}) {
  const addMember = () =>
    setMembers([
      ...members,
      {
        member_id: uid("mbr"),
        name: "",
        role: "adult",
        weekly_hours_available: 30,
      },
    ]);

  const updateMember = (id, patch) =>
    setMembers(members.map((m) => (m.member_id === id ? { ...m, ...patch } : m)));

  const removeMember = (id) =>
    setMembers(members.filter((m) => m.member_id !== id));

  return (
    <div className="max-w-4xl">
      <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-teal">
        01 · Roster
      </p>
      <h1 className="mt-4 font-display text-4xl leading-[1.05] text-ink md:text-5xl">
        Who&apos;s aboard?
      </h1>
      <p className="mt-4 max-w-xl text-base leading-relaxed text-mist">
        Everyone in the household. Weekly hours available means discretionary
        capacity — after sleep, work, and standing care.
      </p>

      <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
            Household name
          </label>
          <input
            value={household_name}
            onChange={(e) => setHousehold(e.target.value)}
            data-testid="onb-household-name"
            placeholder="e.g. The Rivera household"
            className="mt-2 w-full border border-rule bg-obsidian px-4 py-3 text-ink placeholder:text-mist/50 focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
            Household timezone
          </label>
          <select
            value={timezone || "America/New_York"}
            onChange={(e) => setTimezone(e.target.value)}
            data-testid="onb-household-timezone"
            className="mt-2 w-full border border-rule bg-obsidian px-4 py-3 text-ink focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal"
          >
            {[
              "America/New_York",
              "America/Chicago",
              "America/Denver",
              "America/Los_Angeles",
              "America/Anchorage",
              "Pacific/Honolulu",
              "America/Phoenix",
              "America/Halifax",
              "America/Toronto",
              "America/Vancouver",
              "America/Mexico_City",
              "America/Sao_Paulo",
              "America/Buenos_Aires",
              "Europe/London",
              "Europe/Paris",
              "Europe/Berlin",
              "Europe/Madrid",
              "Europe/Rome",
              "Europe/Amsterdam",
              "Europe/Stockholm",
              "Europe/Athens",
              "Europe/Istanbul",
              "Africa/Cairo",
              "Africa/Johannesburg",
              "Asia/Dubai",
              "Asia/Karachi",
              "Asia/Kolkata",
              "Asia/Bangkok",
              "Asia/Singapore",
              "Asia/Hong_Kong",
              "Asia/Tokyo",
              "Asia/Shanghai",
              "Asia/Seoul",
              "Australia/Perth",
              "Australia/Sydney",
              "Pacific/Auckland",
              "UTC",
            ].map((tz) => (
              <option key={tz} value={tz}>
                {tz.replace("_", " ")}
              </option>
            ))}
          </select>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
            Weekly digest ships Sunday 6pm in this timezone.
          </p>
        </div>
      </div>

      {/* Persona picker — drives engine tuning */}
      <div className="mt-6">
        <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
          Household profile · shapes capacity model
        </label>
        <div className="mt-2 flex flex-wrap gap-2" data-testid="onb-persona-chips">
          {[
            ["dual_income", "Dual-income"],
            ["young_family", "Young family"],
            ["school_age", "School-age"],
            ["empty_nest", "Empty nest"],
            ["multigen", "Multi-gen"],
            ["chronic_illness", "Chronic illness / caregiver"],
            ["solo", "Solo"],
            ["other", "Other"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setPersona(key)}
              data-testid={`onb-persona-${key}`}
              className={`border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                persona === key
                  ? "border-teal bg-teal/15 text-ink"
                  : "border-rule text-mist hover:border-teal hover:text-ink"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <ul className="mt-10 space-y-3">
        {members.map((m, i) => (
          <li
            key={m.member_id}
            data-testid={`onb-member-row-${i}`}
            className="grid grid-cols-1 gap-3 border border-rule bg-deep/40 p-5 md:grid-cols-12 md:items-end"
          >
            <div className="md:col-span-4">
              <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                Name
              </label>
              <input
                value={m.name}
                onChange={(e) => updateMember(m.member_id, { name: e.target.value })}
                placeholder="First name"
                data-testid={`onb-member-name-${i}`}
                className="mt-2 w-full border border-rule bg-obsidian px-3 py-2.5 text-ink placeholder:text-mist/50 focus:border-teal focus:outline-none"
              />
            </div>
            <div className="md:col-span-3">
              <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                Role
              </label>
              <select
                value={m.role}
                onChange={(e) => updateMember(m.member_id, { role: e.target.value })}
                data-testid={`onb-member-role-${i}`}
                className="mt-2 w-full appearance-none border border-rule bg-obsidian px-3 py-2.5 text-ink focus:border-teal focus:outline-none"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'><path d='M1 1L6 6L11 1' stroke='%239BA6B5' stroke-width='1.5'/></svg>\")",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 0.75rem center",
                }}
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-4">
              <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                Weekly discretionary hours
              </label>
              <div className="mt-2 flex items-center gap-3">
                <input
                  type="range"
                  min="0"
                  max="80"
                  value={m.weekly_hours_available}
                  onChange={(e) =>
                    updateMember(m.member_id, {
                      weekly_hours_available: Number(e.target.value),
                    })
                  }
                  data-testid={`onb-member-hours-${i}`}
                  className="w-full accent-teal"
                />
                <span className="w-14 text-right font-mono text-sm text-ink">
                  {m.weekly_hours_available}h
                </span>
              </div>
            </div>
            <div className="flex justify-end md:col-span-1">
              <button
                onClick={() => removeMember(m.member_id)}
                disabled={members.length <= 1}
                data-testid={`onb-member-remove-${i}`}
                className="border border-rule p-2.5 text-mist transition-colors duration-300 hover:border-signalRed hover:text-signalRed disabled:cursor-not-allowed disabled:opacity-30"
                aria-label="Remove member"
              >
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
            </div>
          </li>
        ))}
      </ul>

      <button
        onClick={addMember}
        data-testid="onb-add-member-btn"
        className="mt-6 inline-flex items-center gap-2 border border-rule px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-mist transition-colors duration-300 hover:border-teal hover:text-ink"
      >
        <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
        Add a member
      </button>

      <RosterInvite />
    </div>
  );
}

function RosterInvite() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [invites, setInvites] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/household");
      setInvites(data.invites?.filter?.((i) => i.status === "pending") || []);
    } catch (_) {
      /* noop */
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!clean) return;
    setBusy(true);
    try {
      await api.post("/household/invites", { email: clean, role: "member" });
      toast.success(`Invitation sent to ${clean}.`);
      setEmail("");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send invite.";
      toast.error(typeof msg === "string" ? msg : "Could not send invite.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-12 border border-rule bg-deep/30 p-6" data-testid="onb-invite-card">
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-teal">
        <UserPlus className="h-3.5 w-3.5" strokeWidth={1.5} />
        Invite an adult by email (optional)
      </div>
      <p className="mt-3 max-w-2xl text-sm text-mist">
        Partners, co-parents, or another adult in the household. They&rsquo;ll get a Life OS invite,
        sign in with Google, and their calendar folds into your shared signals.
      </p>
      <form onSubmit={submit} className="mt-5 flex flex-col gap-3 md:flex-row md:items-end">
        <div className="flex-1">
          <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="partner@example.com"
            data-testid="onb-invite-email"
            className="mt-2 w-full border border-rule bg-obsidian px-4 py-3 text-ink placeholder:text-mist/50 focus:border-teal focus:outline-none"
          />
        </div>
        <button
          type="submit"
          disabled={busy || !email.trim()}
          data-testid="onb-invite-submit"
          className="inline-flex items-center gap-2 border border-teal bg-teal/20 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:bg-teal/30 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Mail className="h-3.5 w-3.5" strokeWidth={1.5} />
          {busy ? "Dispatching…" : "Send invite"}
        </button>
      </form>
      {loaded && invites.length > 0 && (
        <ul className="mt-5 space-y-2" data-testid="onb-invite-list">
          {invites.map((inv) => (
            <li
              key={inv.invite_id}
              className="flex items-center justify-between border border-rule bg-obsidian/60 px-4 py-2 font-mono text-[11px]"
            >
              <span className="text-ink">{inv.email}</span>
              <span className="uppercase tracking-[0.22em] text-signalAmber">Pending</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StepCommitments({ commitments, setCommitments, members }) {
  const add = () =>
    setCommitments([
      ...commitments,
      {
        commitment_id: uid("cmt"),
        title: "",
        category: "work",
        days: ["mon", "tue", "wed", "thu", "fri"],
        start_min: 540,
        duration_min: 480,
        member_ids: members[0] ? [members[0].member_id] : [],
      },
    ]);
  const update = (id, patch) =>
    setCommitments(commitments.map((c) => (c.commitment_id === id ? { ...c, ...patch } : c)));
  const remove = (id) =>
    setCommitments(commitments.filter((c) => c.commitment_id !== id));

  const preset = (title, category, days, start_min, duration_min) =>
    setCommitments([
      ...commitments,
      {
        commitment_id: uid("cmt"),
        title,
        category,
        days,
        start_min,
        duration_min,
        member_ids: members[0] ? [members[0].member_id] : [],
      },
    ]);

  return (
    <div className="max-w-5xl">
      <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-teal">
        02 · Standing commitments
      </p>
      <h1 className="mt-4 font-display text-4xl leading-[1.05] text-ink md:text-5xl">
        The recurring load.
      </h1>
      <p className="mt-4 max-w-2xl text-base leading-relaxed text-mist">
        Add the commitments that happen most weeks — work blocks, school pick-ups,
        caregiving, sleep windows. Rough is fine; you can refine later.
      </p>

      {/* presets */}
      <div className="mt-8 flex flex-wrap gap-2">
        {[
          ["Weekday work 9–5", "work", ["mon", "tue", "wed", "thu", "fri"], 540, 480],
          ["School drop-off", "care", ["mon", "tue", "wed", "thu", "fri"], 465, 30],
          ["Sleep 10:30 PM – 6:30 AM", "sleep", ["mon", "tue", "wed", "thu", "fri", "sat", "sun"], 1350, 480],
          ["Weekly grocery run", "chores", ["sat"], 600, 120],
        ].map(([label, cat, days, s, d]) => (
          <button
            key={label}
            onClick={() => preset(label, cat, days, s, d)}
            data-testid={`onb-preset-${cat}`}
            className="border border-rule bg-deep/30 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-mist transition-colors duration-300 hover:border-teal hover:text-ink"
          >
            + {label}
          </button>
        ))}
      </div>

      <ul className="mt-10 space-y-4">
        {commitments.length === 0 && (
          <li className="border border-dashed border-rule bg-deep/20 p-8 text-center text-sm text-mist">
            No commitments yet. Use a preset above or add a custom one.
          </li>
        )}
        {commitments.map((c, i) => (
          <li
            key={c.commitment_id}
            data-testid={`onb-commit-row-${i}`}
            className="grid grid-cols-1 gap-4 border border-rule bg-deep/40 p-5 md:grid-cols-12"
          >
            <div className="md:col-span-5">
              <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                Title
              </label>
              <input
                value={c.title}
                onChange={(e) => update(c.commitment_id, { title: e.target.value })}
                placeholder="e.g. Day job"
                data-testid={`onb-commit-title-${i}`}
                className="mt-2 w-full border border-rule bg-obsidian px-3 py-2.5 text-ink placeholder:text-mist/50 focus:border-teal focus:outline-none"
              />
            </div>
            <div className="md:col-span-3">
              <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                Category
              </label>
              <select
                value={c.category}
                onChange={(e) => update(c.commitment_id, { category: e.target.value })}
                data-testid={`onb-commit-cat-${i}`}
                className="mt-2 w-full appearance-none border border-rule bg-obsidian px-3 py-2.5 text-ink focus:border-teal focus:outline-none"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'><path d='M1 1L6 6L11 1' stroke='%239BA6B5' stroke-width='1.5'/></svg>\")",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 0.75rem center",
                }}
              >
                {CATEGORIES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                Start
              </label>
              <input
                type="time"
                value={`${String(Math.floor(c.start_min / 60)).padStart(2, "0")}:${String(c.start_min % 60).padStart(2, "0")}`}
                onChange={(e) => {
                  const [h, m] = e.target.value.split(":").map(Number);
                  update(c.commitment_id, { start_min: h * 60 + m });
                }}
                data-testid={`onb-commit-start-${i}`}
                className="mt-2 w-full border border-rule bg-obsidian px-3 py-2.5 font-mono text-sm text-ink focus:border-teal focus:outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                Duration (min)
              </label>
              <input
                type="number"
                min="10"
                step="10"
                value={c.duration_min}
                onChange={(e) =>
                  update(c.commitment_id, { duration_min: Number(e.target.value) })
                }
                data-testid={`onb-commit-dur-${i}`}
                className="mt-2 w-full border border-rule bg-obsidian px-3 py-2.5 font-mono text-sm text-ink focus:border-teal focus:outline-none"
              />
            </div>

            <div className="md:col-span-11">
              <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                Days
              </label>
              <div className="mt-2">
                <DayPicker
                  value={c.days}
                  onChange={(days) => update(c.commitment_id, { days })}
                  testIdPrefix={`onb-commit-${i}`}
                />
              </div>
            </div>
            <div className="flex items-end justify-end md:col-span-1">
              <button
                onClick={() => remove(c.commitment_id)}
                data-testid={`onb-commit-remove-${i}`}
                className="border border-rule p-2.5 text-mist transition-colors duration-300 hover:border-signalRed hover:text-signalRed"
                aria-label="Remove commitment"
              >
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
            </div>
          </li>
        ))}
      </ul>

      <button
        onClick={add}
        data-testid="onb-add-commit-btn"
        className="mt-6 inline-flex items-center gap-2 border border-rule px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-mist transition-colors duration-300 hover:border-teal hover:text-ink"
      >
        <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
        Add a commitment
      </button>
    </div>
  );
}

function StepRecovery({ windows, setWindows }) {
  const add = () =>
    setWindows([
      ...windows,
      {
        window_id: uid("rec"),
        label: "Defended time",
        days: ["sun"],
        start_min: 840,
        duration_min: 180,
      },
    ]);
  const update = (id, patch) =>
    setWindows(windows.map((w) => (w.window_id === id ? { ...w, ...patch } : w)));
  const remove = (id) => setWindows(windows.filter((w) => w.window_id !== id));

  return (
    <div className="max-w-5xl">
      <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-teal">
        03 · Recovery windows
      </p>
      <h1 className="mt-4 font-display text-4xl leading-[1.05] text-ink md:text-5xl">
        The time you defend.
      </h1>
      <p className="mt-4 max-w-2xl text-base leading-relaxed text-mist">
        These windows are treated as untouchable. The tower blocks new
        commitments here unless you explicitly override. Add at least one.
      </p>

      <div className="mt-8 flex flex-wrap gap-2">
        {[
          ["Sunday reset", ["sun"], 840, 180],
          ["Weekday evenings", ["mon", "tue", "wed", "thu"], 1200, 120],
          ["Saturday morning slow", ["sat"], 480, 120],
        ].map(([label, days, s, d]) => (
          <button
            key={label}
            onClick={() =>
              setWindows([
                ...windows,
                {
                  window_id: uid("rec"),
                  label,
                  days,
                  start_min: s,
                  duration_min: d,
                },
              ])
            }
            data-testid={`onb-rec-preset-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
            className="border border-rule bg-deep/30 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-mist transition-colors duration-300 hover:border-teal hover:text-ink"
          >
            + {label}
          </button>
        ))}
      </div>

      <ul className="mt-10 space-y-4">
        {windows.length === 0 && (
          <li className="border border-dashed border-rule bg-deep/20 p-8 text-center text-sm text-mist">
            No windows yet. Pick a preset above or add a custom one.
          </li>
        )}
        {windows.map((w, i) => (
          <li
            key={w.window_id}
            data-testid={`onb-rec-row-${i}`}
            className="grid grid-cols-1 gap-4 border border-rule bg-deep/40 p-5 md:grid-cols-12"
          >
            <div className="md:col-span-5">
              <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                Label
              </label>
              <input
                value={w.label}
                onChange={(e) => update(w.window_id, { label: e.target.value })}
                data-testid={`onb-rec-label-${i}`}
                className="mt-2 w-full border border-rule bg-obsidian px-3 py-2.5 text-ink focus:border-teal focus:outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                Start
              </label>
              <input
                type="time"
                value={`${String(Math.floor(w.start_min / 60)).padStart(2, "0")}:${String(w.start_min % 60).padStart(2, "0")}`}
                onChange={(e) => {
                  const [h, m] = e.target.value.split(":").map(Number);
                  update(w.window_id, { start_min: h * 60 + m });
                }}
                data-testid={`onb-rec-start-${i}`}
                className="mt-2 w-full border border-rule bg-obsidian px-3 py-2.5 font-mono text-sm text-ink focus:border-teal focus:outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                Duration (min)
              </label>
              <input
                type="number"
                min="15"
                step="15"
                value={w.duration_min}
                onChange={(e) => update(w.window_id, { duration_min: Number(e.target.value) })}
                data-testid={`onb-rec-dur-${i}`}
                className="mt-2 w-full border border-rule bg-obsidian px-3 py-2.5 font-mono text-sm text-ink focus:border-teal focus:outline-none"
              />
            </div>
            <div className="md:col-span-11">
              <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                Days
              </label>
              <div className="mt-2">
                <DayPicker
                  value={w.days}
                  onChange={(days) => update(w.window_id, { days })}
                  testIdPrefix={`onb-rec-${i}`}
                />
              </div>
            </div>
            <div className="flex items-end justify-end md:col-span-1">
              <button
                onClick={() => remove(w.window_id)}
                data-testid={`onb-rec-remove-${i}`}
                className="border border-rule p-2.5 text-mist transition-colors duration-300 hover:border-signalRed hover:text-signalRed"
                aria-label="Remove window"
              >
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
            </div>
          </li>
        ))}
      </ul>

      <button
        onClick={add}
        data-testid="onb-add-rec-btn"
        className="mt-6 inline-flex items-center gap-2 border border-rule px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-mist transition-colors duration-300 hover:border-teal hover:text-ink"
      >
        <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
        Add a recovery window
      </button>
    </div>
  );
}

function StepReady({ profile }) {
  const memberHours = profile.members.reduce(
    (a, m) => a + (m.weekly_hours_available || 0),
    0
  );
  const commitHours = profile.commitments
    .filter((c) => c.category !== "sleep")
    .reduce((a, c) => a + (c.duration_min / 60) * (c.days?.length || 0), 0);
  const recoverHours = profile.recovery_windows.reduce(
    (a, w) => a + (w.duration_min / 60) * (w.days?.length || 0),
    0
  );

  return (
    <div className="max-w-4xl">
      <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-teal">
        Preflight complete
      </p>
      <h1 className="mt-4 font-display text-5xl leading-[1.02] text-ink md:text-6xl">
        Ready for takeoff.
      </h1>
      <p className="mt-6 max-w-2xl text-base leading-relaxed text-mist">
        Here&apos;s what the tower knows about your household. You can revisit
        onboarding any time from the app menu.
      </p>

      <div className="mt-10 grid gap-6 md:grid-cols-3">
        <div className="border border-rule bg-deep/40 p-6">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
            <UserIcon className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
            Roster
          </div>
          <div className="mt-4 font-mono text-4xl text-signalGreen">
            {profile.members.length}
          </div>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
            member{profile.members.length === 1 ? "" : "s"} · {memberHours}h/wk available
          </p>
        </div>
        <div className="border border-rule bg-deep/40 p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
            Standing commitments
          </div>
          <div className="mt-4 font-mono text-4xl text-signalAmber">
            {profile.commitments.length}
          </div>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
            ~{Math.round(commitHours)}h weekly load
          </p>
        </div>
        <div className="border border-rule bg-deep/40 p-6">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
            <Moon className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
            Recovery windows
          </div>
          <div className="mt-4 font-mono text-4xl text-signalGreen">
            {profile.recovery_windows.length}
          </div>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
            ~{Math.round(recoverHours)}h defended
          </p>
        </div>
      </div>

      <div className="mt-10">
        <CalendarConnect variant="inline" />
      </div>
    </div>
  );
}
